import { useMemo } from 'react';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Flex } from 'foundations-components/components/Flex';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { Table } from 'foundations-components/transitional/components/Table';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { useGetQuery } from 'academy-common-lib/__generated__/chirp/com/hubspot/learning/center/playlists/rpc/AuditChirpService.hooks';
import { useUsersForAccount } from '../../../accounts/accountsQueries';
import UserAvatarLink from '../../../myTeam/components/common/UserAvatarLink';
import { getActionLabel, getDetailsLabel, processAuditEntries } from './AuditLogUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const UserCell = ({
  userId,
  usersMap
}) => {
  const user = usersMap.get(userId);
  if (user) {
    return /*#__PURE__*/_jsx(UserAvatarLink, {
      userEmail: user.email,
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`.trim() || user.email
    });
  }
  return /*#__PURE__*/_jsx(Text, {
    children: userId
  });
};
const PlaylistAuditLogModal = ({
  playlistId,
  onClose
}) => {
  const {
    data,
    loading,
    error
  } = useGetQuery({
    variables: {
      playlistId
    }
  });
  const processedEntries = useMemo(() => {
    var _data$auditEntries;
    const auditEntries = (_data$auditEntries = data === null || data === void 0 ? void 0 : data.auditEntries) !== null && _data$auditEntries !== void 0 ? _data$auditEntries : [];
    const processed = processAuditEntries(auditEntries);
    return processed.reverse();
  }, [data === null || data === void 0 ? void 0 : data.auditEntries]);
  const modifyingUserIds = useMemo(() => [...new Set(processedEntries.map(e => e.entry.modifyingUserId))], [processedEntries]);
  const {
    usersForAccount,
    usersForAccountLoading
  } = useUsersForAccount({
    userIds: modifyingUserIds
  });

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const usersMap = useMemo(() => {
    const map = new Map();
    if (usersForAccount !== null && usersForAccount !== void 0 && usersForAccount.users) {
      usersForAccount.users.forEach(user => {
        map.set(user.id, user);
      });
    }
    return map;
  }, [usersForAccount === null || usersForAccount === void 0 ? void 0 : usersForAccount.users]);
  const isLoading = loading || usersForAccountLoading;
  return /*#__PURE__*/_jsx(Modal, {
    width: 1000,
    children: /*#__PURE__*/_jsxs("div", {
      onClick: e => {
        e.stopPropagation();
      },
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h2",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyCommon.playlists.hubspotPlaylists.auditLog.modalTitle"
            })
          }), /*#__PURE__*/_jsx(HelpIcon, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyCommon.playlists.hubspotPlaylists.auditLog.helpTooltip"
            })
          })]
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onClose
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [isLoading && /*#__PURE__*/_jsx(View, {
          blockSize: "200",
          children: /*#__PURE__*/_jsx(LoadingSpinner, {
            grow: true,
            size: "medium"
          })
        }), error && /*#__PURE__*/_jsx(Alert, {
          type: "danger",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.playlists.hubspotPlaylists.auditLog.error"
          })
        }), !isLoading && !error && processedEntries.length === 0 && /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.playlists.hubspotPlaylists.auditLog.noEntries"
          })
        }), !isLoading && !error && processedEntries.length > 0 && /*#__PURE__*/_jsx(ScrollContainer, {
          maxHeight: "60vh",
          scrollDirection: "vertical",
          children: /*#__PURE__*/_jsxs(Table, {
            condensed: true,
            "data-test-id": "audit-log-table",
            children: [/*#__PURE__*/_jsxs("colgroup", {
              children: [/*#__PURE__*/_jsx("col", {
                style: {
                  width: '25%'
                }
              }), /*#__PURE__*/_jsx("col", {
                style: {
                  width: '35%'
                }
              }), /*#__PURE__*/_jsx("col", {
                style: {
                  width: '25%'
                }
              }), /*#__PURE__*/_jsx("col", {
                style: {
                  width: '15%'
                }
              })]
            }), /*#__PURE__*/_jsx("thead", {
              children: /*#__PURE__*/_jsxs("tr", {
                children: [/*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.playlists.hubspotPlaylists.auditLog.columns.action"
                  })
                }), /*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.playlists.hubspotPlaylists.auditLog.columns.details"
                  })
                }), /*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.playlists.hubspotPlaylists.auditLog.columns.user"
                  })
                }), /*#__PURE__*/_jsx("th", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.playlists.hubspotPlaylists.auditLog.columns.dateTime"
                  })
                })]
              })
            }), /*#__PURE__*/_jsx("tbody", {
              children: processedEntries.map((processedEntry, index) => /*#__PURE__*/_jsxs("tr", {
                "data-test-id": `audit-log-row-${index}`,
                children: [/*#__PURE__*/_jsx("td", {
                  children: getActionLabel(processedEntry)
                }), /*#__PURE__*/_jsx("td", {
                  children: getDetailsLabel(processedEntry)
                }), /*#__PURE__*/_jsx("td", {
                  children: /*#__PURE__*/_jsx(UserCell, {
                    userId: processedEntry.entry.modifyingUserId,
                    usersMap: usersMap
                  })
                }), /*#__PURE__*/_jsx("td", {
                  children: /*#__PURE__*/_jsx(FormattedDateTime, {
                    format: "lll",
                    type: "utc",
                    value: processedEntry.entry.modifiedAt
                  })
                })]
              }, `${processedEntry.entry.modifiedAt}-${processedEntry.entry.modifyingUserId}-${index}`))
            })]
          })
        })]
      }), /*#__PURE__*/_jsx(DialogFooter, {
        children: /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          use: "secondary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.common.close"
          })
        })
      })]
    })
  });
};
export default PlaylistAuditLogModal;